import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { CalendarCheck, Save, CheckCircle2, XCircle, Clock4, FileMinus2, Users2, Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const STATUS_META = {
  present: { label: "Présent", color: "bg-emerald-100 text-emerald-700 border-emerald-300", icon: CheckCircle2 },
  absent: { label: "Absent", color: "bg-red-100 text-red-700 border-red-300", icon: XCircle },
  late: { label: "Retard", color: "bg-amber-100 text-amber-800 border-amber-300", icon: Clock4 },
  excused: { label: "Excusé", color: "bg-sky-100 text-sky-700 border-sky-300", icon: FileMinus2 },
};
const ORDER = ["present", "absent", "late", "excused"];

const DAY_INDEX = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };
const DAY_LABEL = { monday: "Lundi", tuesday: "Mardi", wednesday: "Mercredi", thursday: "Jeudi", friday: "Vendredi", saturday: "Samedi", sunday: "Dimanche" };
const DAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

const todayIso = () => {
  // Local-day ISO (YYYY-MM-DD) — avoids UTC shift that would make "today" become "yesterday"
  // in western timezones between 00:00 UTC and 02:00 local.
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const fmtFrDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00Z");
  return d.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" });
};

// Generate every occurrence of `dayKey` (e.g. 'wednesday') between startIso and endIso inclusive.
// Uses UTC to avoid timezone-related off-by-one bugs (a "Tuesday" date in Europe/Paris was becoming
// a "Monday" in UTC when serialised via toISOString().slice(0,10)).
// Dates falling inside any of the provided `holidayRanges` ({start, end}) are excluded.
const generateSlotDates = (dayKey, startIso, endIso, holidayRanges = []) => {
  const dayIdx = DAY_INDEX[dayKey];
  if (dayIdx === undefined || !startIso || !endIso) return [];
  const start = new Date(startIso + "T00:00:00Z");
  const end = new Date(endIso + "T00:00:00Z");
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return [];
  const isHoliday = (iso) => holidayRanges.some((h) => h && h.start && h.end && iso >= h.start && iso <= h.end);
  // Move start forward to the first matching weekday (UTC)
  const diff = (dayIdx - start.getUTCDay() + 7) % 7;
  const cursor = new Date(start);
  cursor.setUTCDate(cursor.getUTCDate() + diff);
  const out = [];
  while (cursor <= end) {
    const iso = cursor.toISOString().slice(0, 10);
    if (!isHoliday(iso)) out.push(iso);
    cursor.setUTCDate(cursor.getUTCDate() + 7);
  }
  return out;
};

const AttendanceAdmin = () => {
  const { user } = useAuth();
  const restricted = !!user?.restricted;
  const teacherId = user?.teacherId;
  const [slots, setSlots] = useState([]);
  const [settings, setSettings] = useState({});
  const [slotId, setSlotId] = useState("");
  const [date, setDate] = useState("");
  const [roster, setRoster] = useState([]);
  const [marks, setMarks] = useState({}); // enrollmentId -> { status, notes }
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);

  // Load slots + settings once
  useEffect(() => {
    api.get("/content").then(({ data }) => {
      setSettings(data.settings || {});
      const ss = (data.scheduleSlots || []).slice();
      const mine = restricted && teacherId ? ss.filter((s) => s.teacherId === teacherId) : ss;
      mine.sort((a, b) => {
        const da = DAY_ORDER.indexOf(a.day);
        const db = DAY_ORDER.indexOf(b.day);
        if (da !== db) return da - db;
        return (a.startTime || "").localeCompare(b.startTime || "");
      });
      setSlots(mine);
      if (mine.length && !slotId) setSlotId(mine[0].id);
    }).catch(() => toast.error("Impossible de charger les créneaux"));
  }, [restricted, teacherId]);

  const currentSlot = slots.find((s) => s.id === slotId);

  // Compute all dates of the season matching the slot's weekday, excluding school holidays.
  const seasonDates = useMemo(() => {
    if (!currentSlot) return [];
    const startIso = settings.seasonStart || "2026-09-01";
    const endIso = settings.seasonEnd || "2027-06-30";
    const holidays = Array.isArray(settings.holidays) ? settings.holidays : [];
    return generateSlotDates(currentSlot.day, startIso, endIso, holidays);
  }, [currentSlot, settings]);

  // Auto-pick a sensible default date when the slot changes: closest past or today, else first future
  useEffect(() => {
    if (!seasonDates.length) { setDate(""); return; }
    const today = todayIso();
    const past = seasonDates.filter((d) => d <= today);
    const future = seasonDates.filter((d) => d > today);
    const picked = past.length ? past[past.length - 1] : future[0];
    setDate(picked);
  }, [slotId, seasonDates]);

  // Load roster + existing marks when slot/date changes
  useEffect(() => {
    if (!slotId || !date) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get(`/admin/slots/${slotId}/roster`),
      api.get("/admin/attendance", { params: { slot_id: slotId, date } }),
    ]).then(([rosterRes, attRes]) => {
      if (cancelled) return;
      setRoster(rosterRes.data?.roster || []);
      const existing = {};
      (attRes.data || []).forEach((a) => {
        existing[a.enrollmentId] = { status: a.status, notes: a.notes || "" };
      });
      setMarks(existing);
    }).catch(() => toast.error("Erreur de chargement"))
    .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [slotId, date]);

  // Load history for the selected slot
  useEffect(() => {
    if (!slotId) return;
    api.get("/admin/attendance", { params: { slot_id: slotId } })
      .then(({ data }) => setHistory(data || []))
      .catch(() => {});
  }, [slotId]);

  const setStatus = (eid, status) => setMarks((m) => ({ ...m, [eid]: { ...(m[eid] || {}), status } }));
  const setNote = (eid, notes) => setMarks((m) => ({ ...m, [eid]: { ...(m[eid] || {}), notes } }));
  const markAll = (status) => {
    const next = { ...marks };
    roster.forEach((r) => { next[r.enrollmentId] = { ...(next[r.enrollmentId] || {}), status }; });
    setMarks(next);
  };

  const save = async () => {
    if (!slotId || !date) return;
    const payload = {
      slotId, date,
      marks: roster
        .filter((r) => marks[r.enrollmentId]?.status)
        .map((r) => ({
          enrollmentId: r.enrollmentId,
          status: marks[r.enrollmentId].status,
          notes: marks[r.enrollmentId].notes || "",
        })),
    };
    if (payload.marks.length === 0) { toast.error("Aucune présence à enregistrer"); return; }
    setSaving(true);
    try {
      await api.post("/admin/attendance", payload);
      toast.success(`${payload.marks.length} présence(s) enregistrée(s)`);
      const { data } = await api.get("/admin/attendance", { params: { slot_id: slotId } });
      setHistory(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  // History per date
  const historyByDate = useMemo(() => {
    const m = {};
    history.forEach((h) => {
      if (!m[h.date]) m[h.date] = { present: 0, absent: 0, late: 0, excused: 0, total: 0 };
      m[h.date].total += 1;
      if (m[h.date][h.status] !== undefined) m[h.date][h.status] += 1;
    });
    return m;
  }, [history]);

  // Navigation prev / next session
  const dateIdx = seasonDates.indexOf(date);
  const prevDate = dateIdx > 0 ? seasonDates[dateIdx - 1] : null;
  const nextDate = dateIdx >= 0 && dateIdx < seasonDates.length - 1 ? seasonDates[dateIdx + 1] : null;

  const markedCount = roster.filter((r) => marks[r.enrollmentId]?.status).length;

  return (
    <div data-testid="attendance-admin" className="pb-24 lg:pb-0">
      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div>
          <h1 className="font-serif-display text-2xl sm:text-3xl text-stone-800">Présences / Absences</h1>
          <p className="text-stone-500 mt-1 text-sm sm:text-base">Sélectionnez un créneau du planning et une date pour faire l&apos;appel.</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-5 mb-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-1">
            <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">Créneau du planning</label>
            <select
              value={slotId}
              onChange={(e) => setSlotId(e.target.value)}
              data-testid="attendance-slot-select"
              className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blush"
            >
              {slots.length === 0 && <option value="">Aucun créneau</option>}
              {DAY_ORDER.filter((d) => slots.some((s) => s.day === d)).map((d) => (
                <optgroup key={d} label={DAY_LABEL[d]}>
                  {slots.filter((s) => s.day === d).map((s) => (
                    <option key={s.id} value={s.id}>{s.startTime}–{s.endTime} · {s.title}{s.teacherName ? ` (${s.teacherName})` : ""}</option>
                  ))}
                </optgroup>
              ))}
            </select>
            {restricted && slots.length === 0 && (
              <p className="text-xs text-deep-rose mt-1.5" data-testid="attendance-no-slots-hint">
                Aucun créneau ne vous est attribué dans le planning. Demandez à l'administrateur de vous associer à vos cours (Planning des créneaux → professeur).
              </p>
            )}
          </div>
          <div className="md:col-span-1">
            <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">Date de la séance</label>
            <div className="flex items-stretch gap-1">
              <button onClick={() => prevDate && setDate(prevDate)} disabled={!prevDate} className="px-2 bg-stone-100 hover:bg-stone-200 rounded-l-lg disabled:opacity-30" data-testid="attendance-prev-date"><ChevronLeft size={16}/></button>
              <select
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="attendance-date-select"
                className="flex-1 bg-stone-50 border-y border-stone-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blush"
              >
                {seasonDates.length === 0 && <option value="">—</option>}
                {seasonDates.map((d) => {
                  const stats = historyByDate[d];
                  const marker = stats ? ` ✓ (${stats.total})` : "";
                  const today = todayIso();
                  const tag = d === today ? " — aujourd'hui" : (d < today ? " — passée" : "");
                  return <option key={d} value={d}>{fmtFrDate(d)}{tag}{marker}</option>;
                })}
              </select>
              <button onClick={() => nextDate && setDate(nextDate)} disabled={!nextDate} className="px-2 bg-stone-100 hover:bg-stone-200 rounded-r-lg disabled:opacity-30" data-testid="attendance-next-date"><ChevronRight size={16}/></button>
            </div>
            {currentSlot && (
              <div className="text-xs text-stone-500 mt-1.5 flex items-center gap-1.5">
                <Calendar size={11}/> {seasonDates.length} séance(s) de saison · {dateIdx >= 0 ? `${dateIdx + 1}ème` : "—"}
                {Array.isArray(settings.holidays) && settings.holidays.length > 0 && (
                  <span className="text-amber-700">· vacances scolaires exclues</span>
                )}
              </div>
            )}
          </div>
          <div className="md:col-span-1 flex items-end gap-2">
            <button onClick={() => markAll("present")} className="flex-1 md:flex-initial justify-center text-xs sm:text-sm bg-emerald-50 text-emerald-700 hover:bg-emerald-100 px-3 py-2.5 rounded-full inline-flex items-center gap-1.5 font-medium" data-testid="attendance-mark-all-present">
              <CheckCircle2 size={16}/> Tous présents
            </button>
            <button onClick={() => markAll("absent")} className="flex-1 md:flex-initial justify-center text-xs sm:text-sm bg-red-50 text-red-700 hover:bg-red-100 px-3 py-2.5 rounded-full inline-flex items-center gap-1.5 font-medium" data-testid="attendance-mark-all-absent">
              <XCircle size={16}/> Tous absents
            </button>
          </div>
        </div>
      </div>

      {/* Roster */}
      <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-5 mb-5">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Users2 size={16} className="text-deep-rose" />
          <h2 className="font-serif-display text-lg text-stone-800">
            Élèves inscrits
            {currentSlot && <span className="text-stone-500 text-sm font-normal"> — {currentSlot.title} ({DAY_LABEL[currentSlot.day]} {currentSlot.startTime}–{currentSlot.endTime})</span>}
          </h2>
          {roster.length > 0 && (
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ml-auto ${markedCount === roster.length ? "bg-emerald-100 text-emerald-700" : "bg-blush-light text-deep-rose"}`} data-testid="attendance-progress">
              {markedCount}/{roster.length} marqués
            </span>
          )}
        </div>
        {loading ? (
          <div className="text-stone-500 p-6 text-center">Chargement...</div>
        ) : roster.length === 0 ? (
          <div className="text-stone-500 p-6 text-center text-sm">Aucun élève inscrit à ce créneau pour la saison.</div>
        ) : (
          <div className="space-y-2">
            {roster.map((r) => {
              const cur = marks[r.enrollmentId]?.status;
              return (
                <div key={r.enrollmentId} className="border border-stone-100 rounded-xl p-3 sm:p-4" data-testid={`roster-row-${r.enrollmentId}`}>
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="font-serif-display text-stone-800">{r.studentFirstName} {r.studentLastName}</div>
                      <div className="text-stone-500 text-xs">{r.familyName} · {r.familyEmail}</div>
                    </div>
                    <div className="grid grid-cols-4 gap-1.5 w-full mt-1 sm:mt-0 sm:flex sm:w-auto sm:flex-wrap sm:shrink-0">
                      {ORDER.map((st) => {
                        const meta = STATUS_META[st];
                        const Icon = meta.icon;
                        const active = cur === st;
                        return (
                          <button
                            key={st}
                            onClick={() => setStatus(r.enrollmentId, st)}
                            data-testid={`attendance-mark-${st}-${r.enrollmentId}`}
                            className={`inline-flex flex-col sm:flex-row items-center justify-center gap-1 text-xs px-2 py-2.5 sm:py-1.5 rounded-xl sm:rounded-full border transition-all ${active ? meta.color + " font-semibold" : "bg-white text-stone-500 border-stone-200 hover:border-stone-400 active:bg-stone-50"}`}
                          >
                            <Icon size={16} className="sm:w-3 sm:h-3"/> {meta.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {cur && (
                    <input
                      type="text"
                      placeholder="Note (optionnel)"
                      value={marks[r.enrollmentId]?.notes || ""}
                      onChange={(e) => setNote(r.enrollmentId, e.target.value)}
                      className="mt-2 w-full text-sm bg-stone-50 border border-stone-200 rounded px-3 py-1.5"
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
        {roster.length > 0 && (
          <div className="mt-4 hidden lg:flex justify-end">
            <button
              onClick={save}
              disabled={saving}
              data-testid="attendance-save-button"
              className="inline-flex items-center gap-2 bg-coral text-white px-5 py-2 rounded-full text-sm disabled:opacity-50"
            >
              <Save size={16}/> {saving ? "Enregistrement..." : "Enregistrer l'appel"}
            </button>
          </div>
        )}
      </div>

      {/* Sticky mobile save bar — always reachable for quick roll-call at the studio */}
      {roster.length > 0 && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur border-t border-stone-200 px-4 py-3 flex items-center gap-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
          <div className="text-sm text-stone-600 shrink-0">
            <span className="font-semibold text-stone-800">{markedCount}</span>/{roster.length}
          </div>
          <button
            onClick={save}
            disabled={saving}
            data-testid="attendance-save-mobile"
            className="flex-1 justify-center inline-flex items-center gap-2 bg-coral text-white px-5 py-3 rounded-full text-sm font-semibold disabled:opacity-50 active:brightness-95"
          >
            <Save size={18}/> {saving ? "Enregistrement..." : "Enregistrer l'appel"}
          </button>
        </div>
      )}

      {/* History */}
      <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-3">
          <CalendarCheck size={16} className="text-deep-rose" />
          <h2 className="font-serif-display text-lg text-stone-800">Historique des séances</h2>
        </div>
        {Object.keys(historyByDate).length === 0 ? (
          <div className="text-stone-500 p-4 text-center text-sm">Pas encore d&apos;appel enregistré pour ce créneau.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {Object.entries(historyByDate).sort(([a], [b]) => b.localeCompare(a)).map(([d, stats]) => (
              <button
                key={d}
                onClick={() => setDate(d)}
                className={`text-left border rounded-xl p-3 hover:border-deep-rose transition-colors ${d === date ? "border-deep-rose bg-blush-light/30" : "border-stone-200"}`}
              >
                <div className="font-serif-display text-stone-800 text-sm">{fmtFrDate(d)}</div>
                <div className="text-xs text-stone-500 mt-1 flex gap-2 flex-wrap">
                  <span className="text-emerald-700">{stats.present} présent(s)</span>
                  <span className="text-red-700">{stats.absent} absent(s)</span>
                  {stats.late > 0 && <span className="text-amber-700">{stats.late} retard</span>}
                  {stats.excused > 0 && <span className="text-sky-700">{stats.excused} excusé</span>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendanceAdmin;
